/**
 * Centralized blob URL registry for memory management
 *
 * Automatically tracks and revokes blob URLs when they are no longer needed,
 * preventing memory leaks from orphaned blob URLs.
 *
 * Features:
 * - TTL-based automatic cleanup (5 minutes default)
 * - Manual cleanup on component unmount
 * - Automatic cleanup on page unload
 * - Debug utilities for tracking active URLs
 */

/**
 * Registry entry for a blob URL
 */
interface BlobUrlEntry {
  /** The blob URL */
  url: string;
  /** Timestamp when the URL was created */
  createdAt: number;
}

/**
 * Centralized blob URL registry for memory management
 */
class BlobUrlRegistry {
  private urls = new Map<string, BlobUrlEntry>();
  private cleanupInterval: NodeJS.Timeout | null = null;

  /** Time to keep blob URLs before automatic cleanup (5 minutes) */
  private static readonly TTL_MS = 5 * 60 * 1000;

  /** Cleanup check interval (1 minute) */
  private static readonly CLEANUP_INTERVAL_MS = 60 * 1000;

  constructor() {
    // Start automatic cleanup interval
    this.startCleanupInterval();
  }

  /**
   * Register a blob URL and return cleanup function
   *
   * @param url - Blob URL to register
   * @param key - Optional identifier (e.g., file name)
   * @returns Cleanup function to revoke URL
   *
   * @example
   * const blobUrl = URL.createObjectURL(blob);
   * const cleanup = blobUrlRegistry.register(blobUrl, 'evidence.png');
   * // Later or on unmount:
   * cleanup();
   */
  register(url: string, key?: string): () => void {
    const registryKey = key || url;

    this.urls.set(registryKey, {
      url,
      createdAt: Date.now(),
    });

    // Return cleanup function
    return () => this.revoke(registryKey);
  }

  /**
   * Revoke a blob URL by key
   *
   * @param key - The key used when registering the URL
   */
  revoke(key: string): void {
    const entry = this.urls.get(key);
    if (entry) {
      URL.revokeObjectURL(entry.url);
      this.urls.delete(key);
    }
  }

  /**
   * Revoke all blob URLs
   *
   * Called automatically on page unload
   */
  revokeAll(): void {
    for (const [key] of this.urls) {
      this.revoke(key);
    }
  }

  /**
   * Get active blob URL count (for debugging)
   *
   * @returns Number of currently registered blob URLs
   */
  getActiveCount(): number {
    return this.urls.size;
  }

  /**
   * Start automatic cleanup of expired blob URLs
   */
  private startCleanupInterval(): void {
    if (this.cleanupInterval) return;

    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, BlobUrlRegistry.CLEANUP_INTERVAL_MS);
  }

  /**
   * Clean up expired blob URLs based on TTL
   */
  private cleanupExpired(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, entry] of this.urls) {
      if (now - entry.createdAt > BlobUrlRegistry.TTL_MS) {
        expiredKeys.push(key);
      }
    }

    for (const key of expiredKeys) {
      this.revoke(key);
    }
  }

  /**
   * Stop cleanup interval (for testing or shutdown)
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.revokeAll();
  }
}

// Singleton instance
export const blobUrlRegistry = new BlobUrlRegistry();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    blobUrlRegistry.revokeAll();
  });
}
